(() => {
  const questionnairePanels = ['name', 'gender', 'goal', 'training', 'restrictions'];

  const initializeQuiz = (root) => {
    if (!root || root.dataset.quizInitialized === 'true') return;
    root.dataset.quizInitialized = 'true';

    const panels = [...root.querySelectorAll('[data-quiz-panel]')];
    const progress = root.querySelector('[data-quiz-progress]');
    const progressLabel = root.querySelector('[data-quiz-progress-label]');
    const progressBar = root.querySelector('[data-quiz-progress-bar]');
    const nameInput = root.querySelector('[data-quiz-name]');
    const emailInput = root.querySelector('[data-quiz-email]');

    const setError = (panel, message = '') => {
      const error = panel?.querySelector('[data-quiz-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    };

    const getPanel = (name) => panels.find((panel) => panel.dataset.quizPanel === name);

    const updateProgress = (panelName) => {
      const stepIndex = questionnairePanels.indexOf(panelName);
      const shouldShow = stepIndex >= 0;
      if (progress) progress.hidden = !shouldShow;
      if (!shouldShow) return;
      const step = stepIndex + 1;
      if (progressLabel) progressLabel.textContent = `Step ${step} of ${questionnairePanels.length}`;
      if (progressBar) progressBar.style.width = `${(step / questionnairePanels.length) * 100}%`;
      progress?.setAttribute('aria-valuenow', String(step));
      progress?.setAttribute('aria-valuetext', `Step ${step} of ${questionnairePanels.length}`);
    };

    const showPanel = (panelName, { focus = true } = {}) => {
      panels.forEach((panel) => {
        const active = panel.dataset.quizPanel === panelName;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active) setError(panel);
      });
      updateProgress(panelName);
      if (focus) {
        requestAnimationFrame(() => {
          getPanel(panelName)?.querySelector('[data-quiz-heading]')?.focus({ preventScroll: true });
          root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        });
      }
    };

    const checkedInputs = (panel) => [...panel.querySelectorAll('[data-quiz-option]:checked')];

    const validatePanel = (panel) => {
      if (!panel) return false;
      const panelName = panel.dataset.quizPanel;
      if (panelName === 'name' && !nameInput?.value.trim()) {
        setError(panel, 'Please enter your first name to continue.');
        nameInput?.focus();
        return false;
      }
      if (panel.dataset.requiredGroup && checkedInputs(panel).length === 0) {
        setError(panel, 'Please choose at least one option to continue.');
        panel.querySelector('[data-quiz-option]')?.focus();
        return false;
      }
      if (panelName === 'email' && emailInput?.value && !emailInput.checkValidity()) {
        setError(panel, 'Please enter a valid email address or choose Skip.');
        emailInput.focus();
        return false;
      }
      setError(panel);
      return true;
    };

    const selectedValues = (panelName) => {
      const panel = getPanel(panelName);
      return panel ? checkedInputs(panel).map((input) => input.value) : [];
    };

    const answers = () => {
      const goalInput = getPanel('goal')?.querySelector('[data-quiz-option]:checked');
      return {
        name: nameInput?.value.trim() || '',
        recommendationGender: selectedValues('gender')[0] || '',
        goal: selectedValues('goal')[0] || '',
        goalGroup: goalInput?.dataset.resultGroup || '',
        training: selectedValues('training'),
        restrictions: selectedValues('restrictions'),
        email: emailInput?.value.trim() || ''
      };
    };

    const updateResults = () => {
      const response = answers();
      const heading = getPanel('results')?.querySelector('[data-results-heading-template]');
      if (heading) {
        const template = heading.dataset.resultsHeadingTemplate || '';
        heading.textContent = template.replaceAll('[name]', response.name);
      }
      const cards = [...root.querySelectorAll('[data-recommendation-group]')];
      let visibleCount = 0;
      cards.forEach((card) => {
        const exclusions = (card.dataset.excludedPreferences || '').split(',').map(value => value.trim());
        const compatible = !response.restrictions.some(value => exclusions.includes(value));
        const show = compatible && (card.dataset.recommendationGroup === 'all' || card.dataset.recommendationGroup === response.goalGroup);
        card.hidden = !show;
        if (show) visibleCount += 1;
      });
      const empty = root.querySelector('[data-quiz-empty]');
      if (empty) empty.hidden = visibleCount > 0;
    };

    const submitEmail = async () => {};

    const finish = async ({ skipEmail = false } = {}) => {
      const emailPanel = getPanel('email');
      if (!skipEmail && !validatePanel(emailPanel)) return;
      if (skipEmail && emailInput) emailInput.value = '';
      updateResults();
      showPanel('results');
      if (!skipEmail) await submitEmail();
    };

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quiz-option]');
      if (!input) return;
      const panel = input.closest('[data-quiz-panel]');
      setError(panel);
      if (input.type !== 'checkbox' || panel?.dataset.quizPanel !== 'restrictions') return;
      const options = [...panel.querySelectorAll('[data-quiz-option]')];
      if (input.dataset.exclusiveOption === 'true' && input.checked) {
        options.forEach((option) => { if (option !== input) option.checked = false; });
      } else if (input.checked) {
        options.forEach((option) => { if (option.dataset.exclusiveOption === 'true') option.checked = false; });
      }
    });

    root.addEventListener('click', (event) => {
      const next = event.target.closest('[data-quiz-next]');
      if (next) {
        const panel = next.closest('[data-quiz-panel]');
        if (panel?.dataset.quizPanel === 'welcome' || validatePanel(panel)) { if (next.dataset.quizNext === 'email') { updateResults(); showPanel('results'); } else showPanel(next.dataset.quizNext); }
        return;
      }
      const back = event.target.closest('[data-quiz-back]');
      if (back) {
        showPanel(back.dataset.quizBack);
        return;
      }
      if (event.target.closest('[data-quiz-finish]')) {
        finish();
        return;
      }
      if (event.target.closest('[data-quiz-skip]')) {
        finish({ skipEmail: true });
        return;
      }
      if (event.target.closest('[data-quiz-restart]')) {
        root.querySelectorAll('[data-quiz-option]').forEach((input) => { input.checked = false; });
        if (nameInput) nameInput.value = '';
        if (emailInput) emailInput.value = '';
        showPanel('welcome');
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target === nameInput) {
        event.preventDefault();
        const panel = getPanel('name');
        if (validatePanel(panel)) showPanel('gender');
      } else if (event.target === emailInput) {
        event.preventDefault();
        finish();
      }
    });

    showPanel('welcome', { focus: false });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-tl-quiz]')) initializeQuiz(scope);
    scope.querySelectorAll?.('[data-tl-quiz]').forEach(initializeQuiz);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
